import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { de } from "date-fns/locale";
import clsx from "clsx";
import { apiGet } from "@/lib/api";
import { EVENT_CATEGORIES, normaliseEventCategory } from "@/lib/eventCategories";
import type { EventDto } from "@/types";

interface Props {
  /** How many months to render. Spec: 3 with paging arrows. */
  monthsToShow?: number;
}

/**
 * Sidebar mini-calendar.
 *
 * Updates:
 *  - Days with events are now filled with the colour of the first
 *    matching category (much more visible than tiny dots).
 *  - Category palette aligned with the Termine-Kategorien
 *    (Dorfleben / Veranstaltungen / Vereine / Informationen /
 *    Ortsbeiratssitzungen / uncategorized).
 */
export function SidebarMiniCalendar({ monthsToShow = 3 }: Props) {
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(new Date()));
  const [enabled, setEnabled] = useState<Record<string, boolean>>(
    Object.fromEntries(EVENT_CATEGORIES.map((c) => [c.key, true])),
  );

  const from = useMemo(() => startOfMonth(cursor), [cursor]);
  const to   = useMemo(() => endOfMonth(addMonths(cursor, monthsToShow - 1)), [cursor, monthsToShow]);

  const { data: events = [] } = useQuery<EventDto[]>({
    queryKey: ["sidebar-cal", from.toISOString(), to.toISOString()],
    queryFn: () =>
      apiGet<EventDto[]>(
        `/public/events?locale=de&from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
      ),
    staleTime: 60_000,
  });

  // Bucket by day; remember the category of the FIRST event of the day
  // because that drives the background colour. Up to 5 events per day
  // tracked for the count badge.
  type DayBucket = { count: number; category: string };
  const eventsByDay = useMemo(() => {
    const map = new Map<string, DayBucket>();
    for (const e of events) {
      const cat = normaliseEventCategory(e.category);
      if (cat in enabled && !enabled[cat]) continue;
      const key = format(parseISO(e.startAt), "yyyy-MM-dd");
      const existing = map.get(key);
      if (existing) existing.count += 1;
      else map.set(key, { count: 1, category: cat });
    }
    return map;
  }, [events, enabled]);

  return (
    <section
      aria-label="Kalender-Vorschau"
      className="bg-bg-soft border border-border rounded-lg p-4"
    >
      <header className="flex items-center justify-between mb-3">
        <h2 className="font-serif font-bold text-base text-primary">
          {format(cursor, "LLLL yyyy", { locale: de })}
        </h2>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setCursor((c) => addMonths(c, -1))}
                  aria-label="Vorheriger Monat"
                  className="w-7 h-7 rounded hover:bg-bg-muted text-text-muted hover:text-primary text-sm">&lsaquo;</button>
          <button type="button" onClick={() => setCursor(startOfMonth(new Date()))}
                  className="px-2 h-7 rounded text-xs hover:bg-bg-muted text-text-muted hover:text-primary">Heute</button>
          <button type="button" onClick={() => setCursor((c) => addMonths(c, 1))}
                  aria-label="Naechster Monat"
                  className="w-7 h-7 rounded hover:bg-bg-muted text-text-muted hover:text-primary text-sm">&rsaquo;</button>
        </div>
      </header>

      <div className="space-y-4">
        {Array.from({ length: monthsToShow }, (_, i) => addMonths(cursor, i)).map((m) => (
          <MonthGrid key={m.toISOString()} month={m} eventsByDay={eventsByDay} />
        ))}
      </div>

      <CategoryLegend enabled={enabled} setEnabled={setEnabled} />

      <div className="mt-3 pt-3 border-t border-border text-xs">
        <Link to="/kalender" className="text-primary hover:underline">Alle Termine</Link>
        <span className="mx-2 text-text-muted">|</span>
        <a href="/api/v1/public/events/calendar.ics" className="text-primary hover:underline"
           title="Gesamtkalender als ICS-Datei herunterladen">ICS-Export</a>
      </div>
    </section>
  );
}

function MonthGrid({
  month, eventsByDay,
}: {
  month: Date;
  eventsByDay: Map<string, { count: number; category: string }>;
}) {
  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1 text-center">
        {format(month, "LLLL", { locale: de })}
      </div>
      <div className="grid grid-cols-7 gap-px text-center text-[10px] text-text-muted mb-1">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => (<div key={d}>{d}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const bucket = eventsByDay.get(key);
          const inMonth = isSameMonth(day, month);
          const today = isToday(day);
          const cat = bucket
            ? EVENT_CATEGORIES.find((c) => c.key === bucket.category) ?? EVENT_CATEGORIES[EVENT_CATEGORIES.length - 1]
            : null;
          return (
            <Link
              key={key}
              to={`/kalender?date=${key}`}
              className={clsx(
                "relative flex items-center justify-center h-9 rounded-sm text-xs transition-colors",
                !inMonth && "text-text-muted/40",
                inMonth && !cat && "hover:bg-bg-muted text-text",
                today && "ring-2 ring-accent ring-offset-1 ring-offset-bg-soft",
              )}
              style={cat ? {
                background: cat.colorBg,
                color: cat.colorText,
                fontWeight: 600,
              } : undefined}
              aria-label={
                format(day, "d. LLLL yyyy", { locale: de })
                + (bucket ? ` (${bucket.count} Termin${bucket.count === 1 ? "" : "e"}, ${cat?.label})` : "")
              }
              title={bucket ? `${bucket.count} Termin(e) - ${cat?.label}` : undefined}
            >
              {format(day, "d")}
              {bucket && bucket.count > 1 && (
                <span aria-hidden className="absolute top-0 right-0 text-[8px] px-1 rounded-bl-sm"
                      style={{ background: "rgba(255,255,255,0.7)", color: cat?.colorText }}>
                  {bucket.count}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function CategoryLegend({
  enabled, setEnabled,
}: {
  enabled: Record<string, boolean>;
  setEnabled: (next: Record<string, boolean>) => void;
}) {
  return (
    <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-1.5 text-xs">
      {EVENT_CATEGORIES.map((c) => {
        const active = enabled[c.key] ?? true;
        return (
          <button
            key={c.key} type="button"
            onClick={() => setEnabled({ ...enabled, [c.key]: !active })}
            className={clsx(
              "inline-flex items-center gap-1.5 px-2 py-1 rounded border transition-colors",
              active ? "border-border" : "border-border bg-transparent text-text-muted line-through",
            )}
            style={active ? { background: c.colorBg, color: c.colorText } : undefined}
            aria-pressed={active}
            title={c.label}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}
